import React from 'react';
import styled from '@emotion/styled';
import Popover from '@material-ui/core/Popover';
import { IoIosGlobe } from 'react-icons/io';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  Link,
  devices,
  Content,
  BlueButtonBasic,
  Spacing,
  WhiteButtonLink,
  Icon,
  Text,
} from '../common';
import Logo from '../../../static/assets/logo.png';
import { MAX_HEADER_WIDTH } from '../../constants';
import { ExternalLink, PhoneIcon } from '../SVG';
import theme from '../../theme';
import MobileHeader from './MobileHeader';
import { Links as LinkType } from '../../@types/types';

const Header = () => {
  const links = [
    { name: 'Palvelut ja hinnasto', path: '/palvelut' },
    { name: 'Asiantuntijat', path: '/asiantuntijat' },
    { name: 'Aatoslaiseksi?', path: '/tyopaikat' },
    { name: 'Koulutukset', path: '/koulutukset' },
    { name: 'Työnohjaus', path: '/tyonohjaus' },
    { name: 'UKK', path: '/ukk' },
    { name: 'Blogi', path: '/blogi' },
    { name: 'Yhteys', path: '/yhteys' },
  ];

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const langs = [
    { name: 'Suomeksi', path: '/' },
    { name: 'På svenska', path: '/svenska-tjanster' },
    { name: 'In English', path: '/english-services' },
  ];

  const pathname =
    typeof window !== 'undefined' && window.location && window.location.pathname
      ? window.location.pathname
      : '/';

  const currentLang =
    langs.find((lang) => lang.path === pathname)?.name || 'Suomeksi';

  return (
    <>
      <Container>
        <HeaderContainer align="center" justify="space-between" axis="x">
          <LeftWrapper>
            <Link to="/">
              <NavItem>
                <LogoImage src={Logo} alt="Aatos Klinikka" />
              </NavItem>
            </Link>
            <LinksContainer>
              {links.map(({ name, path }: LinkType, index) => (
                <NavItem key={index}>
                  <NavLink to={path} activeClassName="active">
                    {name}
                  </NavLink>
                </NavItem>
              ))}
            </LinksContainer>
          </LeftWrapper>
          <ExternalLinks>
            <LangWrapper>
              <LangButton
                onClick={handleClick}
                aria-haspopup="true"
                aria-expanded={open}
              >
                <Icon icon={IoIosGlobe} size={24} color="black" />
                <Text variant="body-s-semi-bold">{currentLang}</Text>
                <Icon
                  icon={anchorEl ? FaChevronUp : FaChevronDown}
                  size={16}
                  color="black"
                />
              </LangButton>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                style={{ zIndex: 10001 }}
              >
                <PopoverContent>
                  {langs.map(({ name, path }) => (
                    <NavLink
                      key={name}
                      to={path}
                      $selected={currentLang === name}
                    >
                      <PopoverItem onClick={handleClose}>{name}</PopoverItem>
                    </NavLink>
                  ))}
                </PopoverContent>
              </Popover>
            </LangWrapper>
            <Spacing axis="x" amount="default" />
            <CustomBlueButton
              href="https://www.nordhealth.fi/fi/yritys/aatosklinikka"
              target="_blank"
            >
              Nettiajanvaraus
              <ExternalLinkIcon fill={theme.colors.white} />
            </CustomBlueButton>
            <WhiteButtonLink
              href="tel:+358103254540"
              style={{ textDecoration: 'none' }}
            >
              Soita &nbsp;
              <CustonPhoneIcon fill={theme.colors.darkBlue} />
            </WhiteButtonLink>
          </ExternalLinks>
        </HeaderContainer>
      </Container>

      <MobileHeader links={links} />
    </>
  );
};
export default Header;

const Container = styled.div`
  position: fixed;
  z-index: 10000;
  background-color: ${(props) => props.theme.colors.white};
  width: 100%;
  box-shadow: 0px 3px 6px #0000000b;
`;

const HeaderContainer = styled(Content)`
  height: 100px;
  max-width: ${MAX_HEADER_WIDTH};

  a {
    text-decoration: none;
  }

  @media ${devices.laptopL} {
    width: 95% !important;
  }

  @media (max-width: 1320px) {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style-type: none;
  margin-right: 20px;
  width: max-content;

  &:last-child {
    margin-right: 0px;
  }

  @media ${devices.mobileL} {
    margin-right: 45px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  margin: 20px;
`;

const NavLink = styled(Link)<{ $selected?: boolean }>`
  text-decoration: none;
  color: ${(props) => props.theme.colors.black};
  ${(props) => props.theme.typography['body-small']}
  ${({ $selected }) => $selected && `font-weight: bold;`}
  width: 100%;

  &.active {
    border-bottom: 3px solid ${(p) => p.theme.colors.blue};
    padding: 37px 0px;
    font-weight: bold;
  }
`;

const ExternalLinks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0px !important;
`;

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const ExternalLinkIcon = styled(ExternalLink)`
  margin-left: 10px;
`;

const CustomBlueButton = styled(BlueButtonBasic)`
  width: max-content;
  font-size: ${(props) => props.theme.sizing.default};
  font-weight: bold;

  @media ${devices.tablet} {
    margin-right: 0;
    align-self: center;
  }
`;

const LogoImage = styled.img`
  width: 180px;
  margin: 0px 0px 0px 0px;
`;

const CustonPhoneIcon = styled(PhoneIcon)`
  margin-left: 10px;
`;

const LangWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 160px;
`;

const LangButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 8px;
  color: ${(p) => p.theme.colors.black};
  gap: ${(p) => p.theme.sizing.small};
  font-size: ${(p) => p.theme.sizing.default};
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 145px;
  background-color: ${(p) => p.theme.colors.lightGrey};
  border-radius: ${(p) => p.theme.sizing.small};
`;

const PopoverItem = styled.div`
  padding: 8px 20px;
  white-space: nowrap;
  &:hover {
    background: ${(p) => p.theme.colors.selectedGreyDark};
  }
`;
